/**
 * Globaler axios-Interceptor fuer den automatischen Token-Refresh (T-009, 28.08.2026).
 *
 * Hintergrund: Access-Tokens laufen nach 15 Minuten ab. Komponenten, die axios
 * direkt mit dem localStorage-Token aufrufen (InvoiceManagement, Settings,
 * adminService, CalendarService), liefen danach in 401-Antworten mit leerem Body.
 * fetch()-Aufrufe sind ueber authFetch (api.config.ts) abgedeckt, dieser
 * Interceptor schliesst die axios-Luecke ZENTRAL, ohne jede Datei anzufassen.
 *
 * Verhalten:
 * 1. Request-Interceptor: Wenn der Aufruf bereits einen Authorization-Header
 *    setzt (also authentifiziert sein WILL), wird er mit dem aktuellen Token aus
 *    localStorage ueberschrieben. Anonyme Aufrufe (Kundenflow) bleiben unberuehrt.
 * 2. Response-Interceptor: Bei 401 genau EIN Refresh-Versuch (single-flight via
 *    refreshAccessToken aus api.config.ts), danach Wiederholung des Requests mit
 *    dem neuen Token. Schlaegt der Refresh fehl, wird der urspruengliche
 *    401-Fehler unveraendert weitergereicht (bestehende Fehlerbehandlung greift).
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { refreshAccessToken } from './api.config';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _authRetry?: boolean;
}

export const setupAxiosAuthInterceptor = (): void => {
  axios.interceptors.request.use((config) => {
    // Nur Aufrufe anfassen, die bereits authentifiziert sein wollen
    if (config.headers && config.headers.Authorization) {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableRequestConfig | undefined;
      const hadAuthHeader = !!config?.headers?.Authorization;
      if (error.response?.status === 401 && config && hadAuthHeader && !config._authRetry) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          config._authRetry = true;
          const token = localStorage.getItem('authToken');
          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return axios(config);
        }
      }
      return Promise.reject(error);
    }
  );
};
